import { useState, useRef, useEffect } from 'react'
import { motion } from 'framer-motion'
import { ShieldCheck, Loader2, ArrowLeft } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { useNavigate } from 'react-router-dom'

export function TwoFactorVerifyPage() {
  const { pendingTwoFactor, verify2FA } = useAuth()
  const navigate = useNavigate()
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Redirect if no pending 2FA
  useEffect(() => {
    if (!pendingTwoFactor || pendingTwoFactor.type !== '2fa') {
      navigate('/login', { replace: true })
    }
  }, [pendingTwoFactor, navigate])

  // Focus first input on mount
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  const handleChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value.slice(-1)
    setCode(newCode)
    setError('')

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all 6 digits entered
    const fullCode = newCode.join('')
    if (fullCode.length === 6) {
      handleSubmit(fullCode)
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pasted.length === 6) {
      const newCode = pasted.split('')
      setCode(newCode)
      handleSubmit(pasted)
    }
  }

  const handleSubmit = async (fullCode?: string) => {
    const codeStr = fullCode || code.join('')
    if (codeStr.length !== 6) return

    setIsLoading(true)
    setError('')
    try {
      await verify2FA(codeStr)
    } catch {
      setError('Invalid code. Please try again.')
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  if (!pendingTwoFactor) return null

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-orange-50 dark:from-[#000742] dark:via-[#1a1f5c] dark:to-[#2a2f6c]" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md mx-4"
      >
        <div className="bg-white/90 dark:bg-[#000742]/90 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-white/20 dark:border-[#5869ff]/20">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-[#5869ff]/10 dark:bg-[#5869ff]/20 rounded-full flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-[#5869ff]" />
            </div>
            <h1 className="text-2xl font-bold text-[#000742] dark:text-white">
              Two-Factor Authentication
            </h1>
            <p className="text-[#475467] dark:text-gray-400 mt-2">
              Enter the 6-digit code from your authenticator app
            </p>
          </div>

          {/* Code inputs */}
          <div className="flex justify-center gap-3 mb-6">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={(el) => { inputRefs.current[index] = el }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                disabled={isLoading}
                className={`w-12 h-14 text-center text-xl font-bold rounded-lg border-2 transition-all
                  ${error
                    ? 'border-red-400 dark:border-red-500'
                    : 'border-gray-300 dark:border-[#5869ff]/30 focus:border-[#5869ff] dark:focus:border-[#5869ff]'
                  }
                  bg-white/50 dark:bg-[#1a1f5c]/30 text-gray-900 dark:text-white
                  focus:ring-2 focus:ring-[#5869ff]/20 focus:outline-none
                  disabled:opacity-50`}
              />
            ))}
          </div>

          {/* Error message */}
          {error && (
            <p className="text-center text-sm text-red-500 dark:text-red-400 mb-4">
              {error}
            </p>
          )}

          {/* Submit button */}
          <button
            onClick={() => handleSubmit()}
            disabled={isLoading || code.join('').length !== 6}
            className="w-full py-3 px-4 bg-gradient-to-r from-[#5869ff] to-[#000742] text-white rounded-lg font-medium hover:from-[#f8724f] hover:to-[#e85d3a] focus:outline-none focus:ring-2 focus:ring-[#5869ff] focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </button>

          {/* Back to login */}
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="w-full mt-4 py-2 text-sm text-[#475467] dark:text-gray-400 hover:text-[#5869ff] transition-colors flex items-center justify-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </button>
        </div>
      </motion.div>
    </div>
  )
}
